import React, { useContext, useState, useEffect } from "react";
import useSWR, { mutate } from "swr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCheck,
  faUserTimes,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import TableTotalNCost from "./TableTotalNCost";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import SearchFilter from "./SearchFilter";
import { NavigationContext } from "navigation-react";
import LoaderAnimation from "./LoaderAnimation";

// Helper: Parse date safely
function parseDate(dateStr) {
  if (!dateStr) return null;
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) return isoDate;

  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    const parsed = new Date(year, month - 1, day);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

// Base URL from env
const BASE_URL = process.env.REACT_APP_DRUPAL_BASE_URL;
const STAFF_API = `${BASE_URL}/staff-signin-signout-api`;

// Fetcher
const fetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return res.json();
};

const StaffSignRegister = () => {
  // eslint-disable-next-line no-unused-vars
  const { stateNavigator } = useContext(NavigationContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [requestStartDate, setRequestStartDate] = useState("");
  const [requestEndDate, setRequestEndDate] = useState("");

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => mutate(STAFF_API), 5000);
    return () => clearInterval(interval);
  }, []);

  const { data, error, isLoading } = useSWR(STAFF_API, fetcher);

  if (error)
    return (
      <LoaderAnimation loadingTxt="Failed to load staff records. Check your connection." />
    );
  if (isLoading)
    return <LoaderAnimation loadingTxt="Loading staff attendance..." />;

  const staffList = Array.isArray(data) ? data : [];

  // Filtered staff data
  const filteredData = staffList.filter((staff) => {
    const isFilterEmpty =
      !searchTerm.trim() &&
      !statusFilter &&
      !requestStartDate &&
      !requestEndDate;
    if (isFilterEmpty) return true;

    const search = searchTerm.trim().toLowerCase();

    const fullName = `${staff.field_firist_name || ""} ${
      staff.field_last_name || ""
    }`.toLowerCase();
    const matchesFullName = fullName.includes(search);
    const matchesStaffID = (staff.field_staff_id || "")
      .toLowerCase()
      .includes(search);
    const matchesDepartment = (staff.field_department || "")
      .toLowerCase()
      .includes(search);
    const matchesPhone = (staff.field_phone_number || "")
      .toLowerCase()
      .includes(search);

    const matchesSearchTerm =
      matchesFullName || matchesStaffID || matchesDepartment || matchesPhone;

    const matchesStatus =
      !statusFilter ||
      staff.field_staff_status?.toLowerCase().trim() ===
        statusFilter.toLowerCase().trim();

    // Date range filtering
    const staffDate = parseDate(staff.created);
    const startDate = requestStartDate ? new Date(requestStartDate) : null;
    const endDate = requestEndDate ? new Date(requestEndDate) : null;

    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);
    if (staffDate) staffDate.setHours(12, 0, 0, 0);

    const isInRange =
      (!startDate || (staffDate && staffDate >= startDate)) &&
      (!endDate || (staffDate && staffDate <= endDate));

    return matchesSearchTerm && matchesStatus && isInRange;
  });

  // Stats
  const totalAttendance = filteredData.length;
  const signedIn = filteredData.filter(
    (s) => s.field_staff_status?.toLowerCase() === "sign in"
  ).length;
  const signedOut = filteredData.filter(
    (s) => s.field_staff_status?.toLowerCase() === "sign out"
  ).length;

  const iconStyle = (color) => ({
    backgroundColor: color,
    borderRadius: "50%",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  });

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="patient-register">
          <TopActionMenus AddTitle="Staff Attendance Register" />

          {/* Stats */}
          <div
            style={{
              display: "flex",
              gap: "20px",
              margin: "20px 0",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                flex: 1,
                padding: "24px",
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                display: "flex",
                gap: "15px",
                alignItems: "center",
              }}
            >
              <div style={iconStyle("#faca2b")}>
                <FontAwesomeIcon icon={faUserCheck} size="lg" color="#fff" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", color: "#333" }}>
                  Signed In
                </h3>
                <p style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {signedIn}
                </p>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                padding: "24px",
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                display: "flex",
                gap: "15px",
                alignItems: "center",
              }}
            >
              <div style={iconStyle("#05A550")}>
                <FontAwesomeIcon icon={faUserTimes} size="lg" color="#fff" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", color: "#333" }}>
                  Signed Out
                </h3>
                <p style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {signedOut}
                </p>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                padding: "24px",
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                display: "flex",
                gap: "15px",
                alignItems: "center",
              }}
            >
              <div style={iconStyle("#007bff")}>
                <FontAwesomeIcon icon={faUsers} size="lg" color="#fff" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", color: "#333" }}>
                  Total Attendance
                </h3>
                <p style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {totalAttendance}
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <SearchFilter
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            requestStartDate={requestStartDate}
            setRequestStartDate={setRequestStartDate}
            requestEndDate={requestEndDate}
            setRequestEndDate={setRequestEndDate}
            statusOptions={["Sign In", "Sign Out"]}
          />

          {/* Table */}
          {filteredData.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Staff ID</th>
                  <th>Full Name</th>
                  <th>Department</th>
                  <th>Sign In Time</th>
                  <th>Sign Out Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((staff, i) => (
                  <tr key={staff?.nid || i}>
                    <td>{staff?.field_staff_id || "-"}</td>
                    <td>{`${staff?.field_firist_name || ""} ${
                      staff?.field_last_name || ""
                    }`}</td>
                    <td>{staff?.field_department || "-"}</td>
                    <td>{staff?.field_staff_sign_in_time || "-"}</td>
                    <td>{staff?.field_staff_sign_out_time || "-"}</td>
                    <td>
                      <div
                        style={{
                          background:
                            staff?.field_staff_status?.toLowerCase() ===
                            "sign in"
                              ? "#faca2b"
                              : "#05A550",
                          color: "#fff",
                          textAlign: "center",
                          borderRadius: 50,
                          padding: "3px 8px",
                        }}
                      >
                        {staff?.field_staff_status || "-"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <LoaderAnimation loadingTxt="No matching staff records found." />
          )}

          {/* Summary */}
          {filteredData.length > 0 && (
            <TableTotalNCost
              noOfReportLable="Number of Staffs:"
              noOfReportLength={filteredData.length}
              totalReportFeeCostLable=""
              totalReportFeeCostValue={null}
            />
          )}
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
};

export default StaffSignRegister;
